import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Pencil, Plus, Ticket, Trash2 } from 'lucide-react';
import { CarSelect } from '../components/forms/EntitySelects';
import { VignetteFormModal, type VignetteDraft } from '../components/forms/VignetteFormModal';
import { Badge, ExpiryBadge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card, EmptyState, PageHeader } from '../components/ui/Card';
import { DataTable, type Column } from '../components/ui/DataTable';
import { FilterBar } from '../components/ui/FilterBar';
import { Select } from '../components/ui/Input';
import { ConfirmDialog } from '../components/ui/Modal';
import { PageLoader } from '../components/ui/Spinner';
import { Tabs } from '../components/ui/Tabs';
import { useDeleteVignette, useVignetteOverview, useVignettes } from '../hooks/useApi';
import { toInt, useUrlFilters } from '../hooks/useUrlFilters';
import { errorMessage } from '../services/api';
import { toast } from '../store';
import type { Vignette, VignetteCountry } from '../types';
import { cn, daysUntil, formatCurrency, formatDate, formatNumber, toDateInput, VIGNETTE_COUNTRIES, VIGNETTE_PERIOD_LABELS } from '../utils/helpers';
import { t, tx } from '../i18n';

const COUNTRIES = Object.keys(VIGNETTE_COUNTRIES) as VignetteCountry[];
const WARN_DAYS = 30;

function BuyLink({ country, className }: { country: VignetteCountry; className?: string }) {
  const info = VIGNETTE_COUNTRIES[country];
  return (
    <a
      href={info.shop}
      target="_blank"
      rel="noopener noreferrer"
      className={cn('inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline', className)}
      title={t('Oficiálny predaj známok – {shopName}', { shopName: info.shopName })}
    >
      
      {t('Kúpiť')} <ExternalLink className="h-3 w-3" aria-hidden="true" />
    </a>
  );
}

export function Vignettes() {
  const [filters, setFilters] = useUrlFilters({ tab: 'overview', car_id: '', country: '', validity: '', page: '1' });
  const [draft, setDraft] = useState<VignetteDraft | null>(null);
  const [editing, setEditing] = useState<Vignette | null>(null);

  return (
    <>
      <PageHeader
        title={t('Diaľničné známky')}
        subtitle={t('Elektronické známky v krajinách EÚ, kde sú pre osobné autá povinné')}
        actions={
          <Button onClick={() => setDraft({})}>
            <Plus className="h-4 w-4" />  {t('Pridať známku')}
          </Button>
        }
      />
      <Tabs
        className="mb-4"
        value={filters.tab}
        onChange={(tab) => setFilters({ tab })}
        tabs={[
          { value: 'overview', label: t('Prehľad vozidiel') },
          { value: 'list', label: t('Všetky známky') },
        ]}
      />
      {filters.tab === 'list' ? <VignetteList filters={filters} setFilters={setFilters} onEdit={setEditing} /> : <CoverageMatrix onAdd={setDraft} />}
      <VignetteFormModal isOpen={!!draft} draft={draft ?? undefined} onClose={() => setDraft(null)} />
      <VignetteFormModal isOpen={!!editing} vignette={editing} onClose={() => setEditing(null)} />
    </>
  );
}

function CoverageMatrix({ onAdd }: { onAdd: (draft: VignetteDraft) => void }) {
  const { data, isLoading } = useVignetteOverview();
  if (isLoading) return <PageLoader />;

  const cell = (carId: number, country: VignetteCountry, vignette: Vignette | undefined) => {
    const days = vignette ? daysUntil(vignette.valid_to) : null;
    // Renewal starts the day after the current one ends (or today if it already ended)
    const nextStart = vignette && days !== null && days >= 0 ? toDateInput(new Date(Date.parse(vignette.valid_to) + 86_400_000)) : toDateInput();
    const tone =
      days === null ? 'text-secondary-400 hover:bg-secondary-50' : days < 0 ? 'bg-red-50 text-red-800 hover:bg-red-100' : days <= WARN_DAYS ? 'bg-amber-50 text-amber-900 hover:bg-amber-100' : 'bg-green-50 text-green-800 hover:bg-green-100';
    return (
      <td key={country} className="p-1 text-center">
        <button
          type="button"
          onClick={() => onAdd({ car_id: carId, country, valid_from: nextStart })}
          className={cn('w-full rounded-md px-2 py-2 text-xs tabular-nums transition-colors', tone)}
          aria-label={vignette ? t('{name}: platná do {validTo} – pridať ďalšiu', { name: VIGNETTE_COUNTRIES[country].name, validTo: formatDate(vignette.valid_to) }) : t('Pridať známku – {name}', { name: VIGNETTE_COUNTRIES[country].name })}
          title={vignette ? t('{name}: {validFrom} – {validTo}. Kliknutím pridáte ďalšiu.', { name: VIGNETTE_COUNTRIES[country].name, validFrom: formatDate(vignette.valid_from), validTo: formatDate(vignette.valid_to) }) : t('Pridať známku – {name}', { name: VIGNETTE_COUNTRIES[country].name })}
        >
          {vignette ? (
            <>
              <span className="block font-medium">{days !== null && days < 0 ? t('neplatná') : `do ${formatDate(vignette.valid_to)}`}</span>
              <span className="block opacity-75">{days !== null && days < 0 ? `od ${formatDate(vignette.valid_to)}` : days !== null && days <= WARN_DAYS ? t('o {days} dní', { days }) : vignette.period ? VIGNETTE_PERIOD_LABELS[vignette.period] : ''}</span>
            </>
          ) : (
            <Plus className="mx-auto h-4 w-4" aria-hidden="true" />
          )}
        </button>
      </td>
    );
  };

  return (
    <Card padding="none">
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th scope="col" className="sticky left-0 z-10 bg-secondary-50">{t('Vozidlo')}</th>
              {COUNTRIES.map((code) => (
                <th key={code} scope="col" className="min-w-[7.5rem] text-center">
                  <span className="block text-sm font-semibold normal-case tracking-normal text-secondary-900">{code}</span>
                  <span className="block text-xs font-normal normal-case tracking-normal text-secondary-500">{VIGNETTE_COUNTRIES[code].name}</span>
                  <BuyLink country={code} className="mt-0.5 normal-case tracking-normal" />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data?.map((row) => (
              <tr key={row.car.id}>
                <td className="sticky left-0 z-10 whitespace-nowrap bg-white">
                  <Link to={`/cars/${row.car.id}`} className="font-mono font-medium text-primary-700 hover:underline">
                    {row.car.license_plate}
                  </Link>
                  <span className="block text-xs text-secondary-500">
                    {row.car.brand} {row.car.model}
                  </span>
                </td>
                {COUNTRIES.map((code) => cell(row.car.id, code, row.countries[code]))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!data?.length && <EmptyState icon={<Ticket className="h-10 w-10" />} title={t('Žiadne aktívne vozidlá')} />}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-secondary-100 px-4 py-3 text-xs text-secondary-600">
        <Legend className="bg-green-100" label={t('platná')} />
        <Legend className="bg-amber-100" label={t('končí do {WARNDAYS} dní', { WARNDAYS: WARN_DAYS })} />
        <Legend className="bg-red-100" label={t('neplatná')} />
        <span>{t('Kliknutím na políčko pridáte známku (obnovenie začne dňom po skončení aktuálnej). „Kúpiť“ otvorí oficiálny štátny e-shop.')}</span>
      </div>
    </Card>
  );
}

function Legend({ className, label }: { className: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn('h-3 w-3 rounded-sm', className)} aria-hidden="true" /> {label}
    </span>
  );
}

type ListFilters = { tab: string; car_id: string; country: string; validity: string; page: string };

function VignetteList({ filters, setFilters, onEdit }: { filters: ListFilters; setFilters: (patch: Partial<ListFilters>) => void; onEdit: (v: Vignette) => void }) {
  const page = toInt(filters.page, 1)!;
  const { data, isLoading, isFetching } = useVignettes({
    car_id: toInt(filters.car_id),
    country: filters.country,
    validity: filters.validity,
    page,
    page_size: 50,
  });
  const [deleting, setDeleting] = useState<Vignette | null>(null);
  const remove = useDeleteVignette();

  const columns: Column<Vignette>[] = [
    {
      key: 'country',
      header: t('Krajina'),
      className: 'whitespace-nowrap',
      render: (v) => (
        <span>
          <Badge variant="primary">{v.country}</Badge> <span className="text-sm">{VIGNETTE_COUNTRIES[v.country]?.name}</span>
        </span>
      ),
    },
    {
      key: 'car',
      header: t('Vozidlo'),
      render: (v) => (
        <Link to={`/cars/${v.car_id}`} className="whitespace-nowrap font-mono font-medium text-primary-700 hover:underline">
          {v.car?.license_plate}
        </Link>
      ),
    },
    { key: 'period', header: t('Typ'), render: (v) => (v.period ? VIGNETTE_PERIOD_LABELS[v.period] : '–') },
    {
      key: 'validity',
      header: t('Platnosť'),
      className: 'whitespace-nowrap',
      render: (v) => (
        <span>
          {formatDate(v.valid_from)} – <ExpiryBadge days={daysUntil(v.valid_to)} date={formatDate(v.valid_to)} warnDays={WARN_DAYS} />
        </span>
      ),
    },
    { key: 'price', header: t('Cena'), className: 'text-right tabular-nums whitespace-nowrap', headerClassName: 'text-right', render: (v) => formatCurrency(v.price) },
    { key: 'reference', header: t('Doklad'), render: (v) => v.reference || '–' },
    {
      key: 'actions',
      header: <span className="sr-only">{t('Akcie')}</span>,
      className: 'text-right whitespace-nowrap',
      render: (v) => (
        <>
          <BuyLink country={v.country} className="mr-2" />
          <button className="icon-btn" onClick={() => onEdit(v)} title={t('Upraviť')} aria-label={t('Upraviť')}>
            <Pencil className="h-4 w-4" />
          </button>
          <button className="icon-btn-danger" onClick={() => setDeleting(v)} title={t('Zmazať')} aria-label={t('Zmazať')}>
            <Trash2 className="h-4 w-4" />
          </button>
        </>
      ),
    },
  ];

  return (
    <Card padding="none">
      <FilterBar>
        <CarSelect label={t('Vozidlo')} wrapperClassName="sm:w-64" placeholder={t('Všetky vozidlá')} value={filters.car_id} onChange={(e) => setFilters({ car_id: e.target.value })} />
        <Select
          label={t('Krajina')}
          wrapperClassName="sm:w-48"
          placeholder={t('Všetky krajiny')}
          options={COUNTRIES.map((c) => ({ value: c, label: `${c} – ${VIGNETTE_COUNTRIES[c].name}` }))}
          value={filters.country}
          onChange={(e) => setFilters({ country: e.target.value })}
        />
        <Select
          label={t('Platnosť')}
          wrapperClassName="sm:w-48"
          placeholder={t('Ľubovoľná')}
          options={[
            { value: 'valid', label: t('Platné dnes') },
            { value: 'expiring', label: t('Končia do {WARNDAYS} dní', { WARNDAYS: WARN_DAYS }) },
            { value: 'expired', label: t('Neplatné') },
          ]}
          value={filters.validity}
          onChange={(e) => setFilters({ validity: e.target.value })}
        />
      </FilterBar>
      {data && (
        <p className="border-b border-secondary-100 px-4 py-2 text-sm text-secondary-600">
          {tx('{count} známok · spolu {sum}', { count: formatNumber(data.total), sum: <strong>{formatCurrency(data.total_price)}</strong> })}
        </p>
      )}
      <DataTable
        data={data?.vignettes}
        columns={columns}
        rowKey={(v) => v.id}
        isLoading={isLoading}
        isFetching={isFetching}
        emptyMessage={<EmptyState icon={<Ticket className="h-10 w-10" />} title={t('Žiadne známky nezodpovedajú filtru')} />}
        pagination={data && { page, pageSize: 50, total: data.total, onPageChange: (p) => setFilters({ page: String(p) }) }}
      />
      <ConfirmDialog
        isOpen={!!deleting}
        onClose={() => setDeleting(null)}
        loading={remove.isPending}
        title={t('Zmazať známku')}
        confirmText={t('Zmazať')}
        message={deleting && t('Zmazať známku {country} pre {plate} ({validFrom} – {validTo})?', { country: deleting.country, plate: deleting.car?.license_plate, validFrom: formatDate(deleting.valid_from), validTo: formatDate(deleting.valid_to) })}
        onConfirm={async () => {
          try {
            await remove.mutateAsync(deleting!.id);
            toast.success(t('Známka bola zmazaná'));
          } catch (err) {
            toast.error(errorMessage(err));
          }
          setDeleting(null);
        }}
      />
    </Card>
  );
}
